/**
 * ehr#584 (Chase 10-21): Preview on a chart file, the way a client previews their files in the
 * portal (portal#66 / portal#136). A picture shows inline, a PDF in the browser's viewer, a text
 * file as text; anything else (Word, Excel, HEIC...) says there's no preview and offers Download.
 * The viewer gets a copy typed for it, never the server's "octet-stream", and an SVG or HTML file
 * is never shown as a page (neither can be uploaded today, but the check stays).
 */
export type FilePreviewKind = 'image' | 'pdf' | 'text' | 'none';

const IMAGE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
};
const TEXT_EXT = /\.(txt|csv|tsv|log|json|xml|md)$/i;
/** A text file is cut here so a big log doesn't freeze the window. */
export const FILE_PREVIEW_TEXT_MAX = 200_000;

const baseMime = (mime: string | null | undefined) =>
  String(mime ?? '')
    .toLowerCase()
    .split(';')[0]!
    .trim();
const ext = (name: string) => (/\.([a-z0-9]+)$/i.exec(name.trim())?.[1] ?? '').toLowerCase();

/** What the viewer shows for a file, from its type and (when the type says little) its name. */
export function filePreviewKind(name: string, mime?: string | null): FilePreviewKind {
  const m = baseMime(mime);
  const e = ext(name);
  if (m === 'image/svg+xml' || e === 'svg' || m === 'text/html' || e === 'html' || e === 'htm')
    return 'none';
  if (m === 'application/pdf' || e === 'pdf') return 'pdf';
  if (/^image\/(png|jpeg|gif|webp|bmp)$/.test(m) || e in IMAGE_TYPES) return 'image';
  if (
    /^text\/(plain|csv|tab-separated-values|markdown)$/.test(m) ||
    /^application\/(json|xml)$/.test(m) ||
    m === 'text/xml' ||
    TEXT_EXT.test(name)
  )
    return 'text';
  return 'none';
}

/** The type the preview copy is made with, so a frame shows a PDF instead of downloading it. */
export function filePreviewMime(name: string, mime?: string | null): string {
  const kind = filePreviewKind(name, mime);
  const m = baseMime(mime);
  if (kind === 'pdf') return 'application/pdf';
  if (kind === 'image') return /^image\//.test(m) ? m : (IMAGE_TYPES[ext(name)] ?? 'image/png');
  if (kind === 'text') return 'text/plain';
  return 'application/octet-stream';
}

/**
 * sec 2026-10-24: a copy of a downloaded file (a portal chat attachment) typed for the viewer. A blob:
 * URL runs in the EHR's own origin, so an HTML, SVG or XML type from the server (the uploader picks
 * it) would run as a page with the staff member's session. Anything without a preview becomes an
 * inert octet-stream.
 */
export function safePreviewBlob(blob: Blob, name: string): Blob {
  return new Blob([blob], { type: filePreviewMime(name, blob.type) });
}

/** base64 → bytes (the API's /files/:id/content answer). */
export function base64Bytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64 || '');
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
